import {
  Box,
  Button,
  Skeleton,
  Tag,
  TagLabel,
  Text,
  useToast,
} from '@chakra-ui/react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactSelect, { SingleValue } from 'react-select';
import { getResourceProviders } from '../../services/resourceProviders/getResourceProviders';

export interface ResourceProviderResult {
  id: string;
  label: string;
  npi?: string;
  specialty?: string;
  tablespace: string;
}

interface ResourceProviderSearchSelectProps {
  onProviderChange: (provider: ResourceProviderResult | null) => void;
  selectedProvider: ResourceProviderResult | null;
  tablespaces: string[];
  placeholder?: string;
  disabled?: boolean;
}

interface SelectOption {
  value: string;
  label: string;
  npi?: string;
  specialty?: string;
  tablespace: string;
}

export const ResourceProviderSearchSelect: React.FC<
  ResourceProviderSearchSelectProps
> = ({
  onProviderChange,
  selectedProvider,
  tablespaces,
  placeholder = 'Select resource provider',
  disabled = false,
}) => {
  const [allProviders, setAllProviders] = useState<
    ResourceProviderResult[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();

  // Fetch all resource providers from all tablespaces - extracted to useCallback for reuse
  const fetchProviders = useCallback(async () => {
    if (!tablespaces || tablespaces.length === 0) {
      setAllProviders([]);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Fetch providers from all tablespaces in parallel
      const fetchPromises = tablespaces.map(async (tablespace) => {
        if (
          !tablespace ||
          typeof tablespace !== 'string' ||
          tablespace.trim() === ''
        ) {
          return { tablespace, providers: [] };
        }

        try {
          const result = await getResourceProviders(tablespace);
          const { data } = result;

          // Handle different possible response structures
          let providersArray: (string | Record<string, unknown>)[] =
            [];

          if (Array.isArray(data)) {
            providersArray = data;
          } else if (
            data?.resourceProviders &&
            Array.isArray(data.resourceProviders)
          ) {
            providersArray = data.resourceProviders;
          } else if (
            data &&
            typeof data === 'object' &&
            'data' in data &&
            Array.isArray(data.data)
          ) {
            providersArray = data.data as (
              | string
              | Record<string, unknown>
            )[];
          } else if (
            data?.providers &&
            Array.isArray(data.providers)
          ) {
            providersArray = data.providers;
          }

          // Map providers with tablespace information
          const providers = providersArray.map(
            (
              provider: string | Record<string, unknown>,
              index: number
            ) => {
              // If provider is a string, use it as both id and label
              if (typeof provider === 'string') {
                return {
                  id: `${tablespace}-${provider}`,
                  label: provider,
                  npi: undefined,
                  specialty: undefined,
                  tablespace,
                };
              }

              // If provider is an object, extract fields
              const providerObj = provider as Record<string, unknown>;
              const id = (providerObj.id ||
                providerObj.providerId ||
                providerObj.resourceProviderId ||
                `provider-${index}`) as string;
              const name = (providerObj.name ||
                providerObj.providerName ||
                providerObj.label ||
                provider ||
                'Unknown Provider') as string;
              const npi = (providerObj.npi || providerObj.NPI) as
                | string
                | undefined;
              const specialty = (providerObj.specialty ||
                providerObj.specialtyName) as string | undefined;

              return {
                id: `${tablespace}-${id}`,
                label: name,
                npi,
                specialty,
                tablespace,
              };
            }
          );

          return { tablespace, providers };
        } catch (err) {
          // Silently handle individual tablespace failures - this is expected
          // Some tablespaces may not have resource providers or the API may be unavailable
          // Log as warning instead of error to reduce console noise
          console.warn(
            `[ResourceProviderSearchSelect] Could not fetch providers for tablespace ${tablespace}:`,
            err instanceof Error ? err.message : 'Unknown error'
          );
          return { tablespace, providers: [] };
        }
      });

      const results = await Promise.all(fetchPromises);

      // Combine all providers from all tablespaces
      const combinedProviders = results.flatMap(
        (result) => result.providers
      );

      // Remove duplicates based on label and tablespace combination
      const uniqueProviders = combinedProviders.reduce(
        (acc, provider) => {
          const key = `${provider.label}-${provider.tablespace}`;
          if (!acc.has(key)) {
            acc.set(key, provider);
          }
          return acc;
        },
        new Map<string, ResourceProviderResult>()
      );

      const providersArray = Array.from(uniqueProviders.values());
      setAllProviders(providersArray);
      setError(null);

      // Only show warning if no providers were found from any tablespace
      // Individual failures are handled silently above
      if (providersArray.length === 0 && tablespaces.length > 0) {
        console.warn('[ResourceProviderSearchSelect] No providers found for any tablespace');
      }
    } catch (err) {
      // This catch handles unexpected errors in Promise.all or processing
      // Individual tablespace failures are already handled above
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Failed to load resource providers';

      console.warn('[ResourceProviderSearchSelect] Unexpected error:', errorMessage);
      setAllProviders([]);
      setError(null); // Don't set error state - allow component to work with empty list

      // Only show toast for unexpected errors, not for individual API failures
      toast({
        title: 'Could not load resource providers',
        description: 'You can still search manually',
        status: 'warning',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  }, [tablespaces, toast]);

  // Fetch providers when tablespaces prop changes
  // Use a ref to track previous tablespaces to prevent unnecessary re-fetches
  // This ensures ResourceProviderSearchSelect only fetches when ITS OWN tablespaces change,
  // not when unrelated tablespaces change in other features (like Department Autofill Helper)
  const prevTablespacesRef = useRef<string[]>([]);
  const tablespacesKey = tablespaces.join(',');

  useEffect(() => {
    // Only fetch if tablespaces actually changed (by value, not reference)
    const tablespacesChanged =
      prevTablespacesRef.current.length !== tablespaces.length ||
      prevTablespacesRef.current.some((ts, idx) => ts !== tablespaces[idx]);

    if (tablespacesChanged && tablespaces.length > 0) {
      prevTablespacesRef.current = [...tablespaces];
      fetchProviders();
    } else if (tablespaces.length === 0) {
      // Clear providers if no tablespaces
      prevTablespacesRef.current = [];
      setAllProviders([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tablespacesKey]); // Use tablespacesKey instead of tablespaces array to prevent unnecessary re-runs

  // Check if selected provider is still available in the fetched providers
  const isSelectedProviderValid = useMemo(() => {
    if (!selectedProvider || allProviders.length === 0) return false;
    // Check if the selected provider exists in the available providers
    // Match by id or by label + tablespace combination
    return allProviders.some(
      (p) =>
        p.id === selectedProvider.id ||
        (p.label === selectedProvider.label &&
          p.tablespace === selectedProvider.tablespace)
    );
  }, [selectedProvider, allProviders]);

  // Convert selected provider to react-select format
  const selectedOption = useMemo(() => {
    if (!selectedProvider) return null;
    // Only show in dropdown if it's valid (exists in available providers)
    if (!isSelectedProviderValid) return null;
    // Include tablespace in label when there are multiple tablespaces
    const labelWithTablespace =
      tablespaces.length > 1
        ? `${selectedProvider.label} (Tablespace: ${selectedProvider.tablespace})`
        : selectedProvider.label;
    return {
      value: selectedProvider.id,
      label: labelWithTablespace,
      npi: selectedProvider.npi,
      specialty: selectedProvider.specialty,
      tablespace: selectedProvider.tablespace,
      originalLabel: selectedProvider.label,
    };
  }, [selectedProvider, tablespaces, isSelectedProviderValid]);

  // Convert all providers to react-select format with tablespace in label
  const options = useMemo(() => {
    return allProviders.map((p) => {
      // Include tablespace in label for clarity
      const labelWithTablespace =
        tablespaces.length > 1
          ? `${p.label} (Tablespace: ${p.tablespace})`
          : p.label;

      return {
        value: p.id,
        label: labelWithTablespace,
        npi: p.npi,
        specialty: p.specialty,
        tablespace: p.tablespace,
        originalLabel: p.label, // Keep original label for display
      };
    });
  }, [allProviders, tablespaces]);

  const handleChange = (selected: SingleValue<SelectOption>) => {
    if (!selected) {
      onProviderChange(null);
      return;
    }

    // Find the original provider from allProviders
    const provider = allProviders.find(
      (p) => p.id === selected.value
    );
    if (provider) {
      onProviderChange(provider);
    }
  };

  if (!tablespaces || tablespaces.length === 0) {
    return (
      <Box
        p="1rem"
        bg="yellow.50"
        borderRadius="md"
        border="1px solid"
        borderColor="yellow.200"
      >
        <Text
          fontSize="sm"
          color="yellow.700"
          fontWeight="500"
          mb="0.5rem"
        >
          Tablespace Required
        </Text>
        <Text fontSize="sm" color="yellow.700">
          Please add a practice in the "Practice Details" section
          below and ensure it has market information. The tablespace
          will be automatically retrieved from the practice's market
          data to enable resource provider selection.
        </Text>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box width="100%">
        <Skeleton height="2.5rem" borderRadius="md" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        p="1rem"
        bg="red.50"
        borderRadius="md"
        border="1px solid"
        borderColor="red.200"
      >
        <Text fontSize="sm" color="red.700">
          {error}
        </Text>
      </Box>
    );
  }

  // Show empty state when no providers are found (but API call succeeded)
  // Don't show empty state if there's a selected provider (even if invalid) - show invalid warning instead
  const showEmptyState =
    !isLoading &&
    !error &&
    options.length === 0 &&
    tablespaces.length > 0 &&
    !selectedProvider;

  return (
    <Box width="100%">
      {/* Warning: Selected provider is no longer available */}
      {selectedProvider &&
        !isSelectedProviderValid &&
        !isLoading &&
        !error && (
          <Box
            p="1rem"
            bg="orange.50"
            borderRadius="md"
            border="1px solid"
            borderColor="orange.200"
            mb="0.75rem"
          >
            <Text
              fontSize="sm"
              color="orange.700"
              fontWeight="500"
              mb="0.25rem"
            >
              Selected Provider No Longer Available
            </Text>
            <Text fontSize="xs" color="orange.600" mb="0.5rem">
              The selected resource provider "{selectedProvider.label}
              " is no longer available for{' '}
              {tablespaces.length === 1
                ? `tablespace "${tablespaces[0]}"`
                : `the selected tablespaces (${tablespaces.join(
                    ', '
                  )})`}
              . This may be because the provider was removed or is no
              longer configured in Athena. Please select a different
              provider or clear the selection.
            </Text>
            <Button
              size="sm"
              colorScheme="orange"
              variant="outline"
              onClick={() => onProviderChange(null)}
            >
              Clear Selection
            </Button>
          </Box>
        )}

      {/* Empty State - No providers found for this tablespace */}
      {showEmptyState && (
        <Box
          p="1rem"
          bg="orange.50"
          borderRadius="md"
          border="1px solid"
          borderColor="orange.200"
          mb="0.75rem"
        >
          <Text
            fontSize="sm"
            color="orange.700"
            fontWeight="500"
            mb="0.25rem"
          >
            No Resource Providers Found
          </Text>
          <Text fontSize="xs" color="orange.600">
            No resource providers are available for{' '}
            {tablespaces.length === 1
              ? `tablespace "${tablespaces[0]}"`
              : `the selected tablespaces (${tablespaces.join(
                  ', '
                )})`}
            . This may be because the practice(s) do not have any
            resource providers configured in Athena.
          </Text>
        </Box>
      )}

      {/* Error State */}
      {error && (
        <Box
          p="1rem"
          bg="red.50"
          borderRadius="md"
          border="1px solid"
          borderColor="red.200"
          mb="0.75rem"
        >
          <Text
            fontSize="sm"
            color="red.700"
            fontWeight="500"
            mb="0.5rem"
          >
            Failed to Load Resource Providers
          </Text>
          <Text fontSize="xs" color="red.600" mb="0.75rem">
            {error}
          </Text>
          <Button
            size="sm"
            colorScheme="red"
            variant="outline"
            onClick={() => fetchProviders()}
            isLoading={isLoading}
            loadingText="Retrying..."
          >
            Retry
          </Button>
        </Box>
      )}

      <ReactSelect
        options={options}
        value={selectedOption}
        onChange={handleChange}
        placeholder={
          options.length > 0
            ? placeholder
            : showEmptyState
            ? 'No providers available'
            : placeholder
        }
        isDisabled={
          disabled ||
          isLoading ||
          Boolean(showEmptyState) ||
          Boolean(selectedProvider && !isSelectedProviderValid)
        }
        isSearchable={true}
        isClearable={true}
        menuPortalTarget={
          typeof document !== 'undefined' ? document.body : undefined
        }
        menuPosition="fixed"
        classNamePrefix="resource-provider-select"
        styles={{
          control: (base) => ({
            ...base,
            minHeight: '2.5rem',
            borderColor: '#CBD5E0',
            '&:hover': {
              borderColor: '#3182CE',
            },
          }),
          placeholder: (base) => ({
            ...base,
            color: '#718096',
            fontSize: '0.95rem',
          }),
          option: (base, state) => ({
            ...base,
            backgroundColor: state.isSelected
              ? '#EBF8FF'
              : state.isFocused
              ? '#F7FAFC'
              : 'white',
            color: state.isSelected ? '#2C5282' : '#2D3748',
          }),
          menu: (base) => ({
            ...base,
            zIndex: 9999,
          }),
          menuPortal: (base) => ({
            ...base,
            zIndex: 9999,
          }),
        }}
        formatOptionLabel={(option: SelectOption) => (
          <Box>
            <Text fontSize="sm" fontWeight="500">
              {(option as SelectOption & { originalLabel?: string })
                .originalLabel || option.label}
            </Text>
            {tablespaces.length > 1 && (
              <Text fontSize="xs" color="gray.600" fontWeight="500">
                Tablespace: {option.tablespace}
              </Text>
            )}
            {option.npi && (
              <Text fontSize="xs" color="gray.600">
                NPI: {option.npi}
              </Text>
            )}
            {option.specialty && (
              <Text fontSize="xs" color="gray.500">
                {option.specialty}
              </Text>
            )}
          </Box>
        )}
        noOptionsMessage={({ inputValue }) =>
          inputValue
            ? `No providers match "${inputValue}"`
            : 'No resource providers found'
        }
      />

      {/* Selected Provider Display - Only show if provider is valid */}
      {selectedProvider && isSelectedProviderValid && (
        <Box mt="0.75rem">
          <Tag
            size="sm"
            colorScheme="purple"
            variant="solid"
            borderRadius="full"
          >
            <TagLabel>
              {selectedProvider.label}
              {tablespaces.length > 1 &&
                ` (Tablespace: ${selectedProvider.tablespace})`}
              {selectedProvider.npi &&
                ` (NPI: ${selectedProvider.npi})`}
            </TagLabel>
          </Tag>
        </Box>
      )}
    </Box>
  );
};
